/**
 * engine/player.ts — the one module that drives the audio element and writes the «плеер»
 * slice.
 *
 * Task 07. Every tap on a track, the play button, the slider, the lock screen and the retry
 * comes here; every event of the element comes here; and `store/player.ts` is written from
 * here and nowhere else. The pieces it wires are each their own module — `unlock.ts` (the
 * first `play()`), `readings.ts` (numbers the element is bad at), `mediasession.ts` (the lock
 * screen) — so this file is the wiring and the decisions, not the mechanics.
 *
 * ## Three things this module must never contain (`spec.md` «Плеер»)
 *
 * 1. **A `visibilitychange` listener.** On Android, minimising the Mini App fires it with
 *    the page hidden — the exact moment the listener expects the music to keep going. The
 *    player listens to Telegram's own `activated` / `deactivated` instead, and those handlers
 *    do not touch the sound either: they save and re-read the state, nothing more.
 * 2. **`requestAnimationFrame`.** It does not fire on a hidden page, so a progress readout or
 *    a watchdog built on it freezes the moment the app is minimised. Progress comes from the
 *    element's events and a plain interval, which Telegram's webviews keep running.
 * 3. **A `play()` before a tap.** Nothing here runs at load; the first `play()` is inside the
 *    listener's own tap, through `unlockWith`.
 *
 * `fdm/no-visibility-pause` and `fdm/no-raf-progress` hold the first two for every file of
 * `src/engine/`; `test/player.test.tsx` holds all three from the outside.
 *
 * ## The watchdog
 *
 * `stalled` fires once, about three seconds after data stops, and never again, so it cannot
 * tell a slow network from a dead one. Liveness is «did the position move or did data arrive
 * recently», polled by the same interval that refreshes the position: twelve seconds without
 * either while the listener wants sound is `stalled`, and the mini player offers «Повторить».
 * A finger holding the record (task 09) is not a stall: its silence is speed 0, chosen.
 * The same twelve seconds bound the request for a fresh URL, which has no timeout of its own.
 * `spec.md` «Плеер»: «сторожевой таймер 12 секунд без прогресса показывает „повторить“».
 *
 * ## The network coming back is one retry (task 43)
 *
 * The window's `online` event, while the player says «Звук не идёт» or «Трек не загрузился» —
 * the two problems that offer «Повторить» — is that tap made for the listener: one retry, with a
 * fresh link, as the button does. A tunnel or a lift then ends without a tap. Anything else —
 * playing, paused, a track gone from the channel, a phone that wants a tap — is left alone, and
 * a retry that fails waits for the next `online` or the listener.
 *
 * ## A sound URL has a life of six hours
 *
 * The catalogue hands each track's URL out with `expiresAt`. A track whose URL is near its
 * end is given a fresh one before it is played — which costs one request, and therefore one
 * `play()` outside the tap; the element is usually unlocked by then, and if it is not, the
 * listener gets «Играть» and taps once. A URL that expired while the element held it shows up
 * as an element error; if the clock says expired, a fresh URL is fetched and the track resumes
 * at the same second, once, without asking.
 *
 * ## The queue (task 08)
 *
 * The player holds exactly the queue's current entry (`store/queue.ts`), and «следующий»,
 * «предыдущий» and the end of a track all move along the queue. The end of a track starts the
 * next one on the same deck with no tap: the deck was unlocked by the first one
 * (`engine/element.ts`). Since task 45 the end of a track is, as a rule, not where the next one
 * starts — see «Two decks».
 *
 * Twenty seconds before the end, the head of the next track is asked for
 * (`engine/prefetch.ts`), once per track. Every ten seconds of play, on a pause, at the end of
 * the queue and when the app is minimised, the state is handed to `persist` — the sync writes
 * it to the server. A launch that restores a queue calls {@link PlayerController.cue}: the
 * track is held at its second without a sound, and the first tap plays it from there.
 *
 * ## The queue goes on down its list (task 44)
 *
 * A queue built by a tap in a list holds the rows that were loaded then, and the label of the
 * list's next page (`store/queue.ts`, `source`). Whenever a track starts with
 * {@link FEED_AHEAD} tracks or fewer left to play, the next page is asked for — once per label,
 * whatever the number of starts meanwhile — and goes on the end of the queue, up to its limit.
 * A page that did not come is asked for again at the next start. «Повтор всего» over a list that
 * is over goes round, as before. A queue restored at launch asks too, when its held track is that
 * near the end: otherwise it would ask only after a track had played through.
 *
 * Shuffled, a page that came is held until the last track of the pass starts, and only then goes
 * in: the queue makes it a pass of its own, after everything already there, which it takes as
 * heard (`store/queue.ts`, «The list it came from»). Handed in earlier, the tracks still ahead
 * would count as heard and be skipped.
 *
 * ## «Really started» (task 11)
 *
 * A track goes to «недавние» when the element says `playing` for it the first time since it
 * was loaded or held — not at the tap. A tap the phone refused, a file that failed, a track
 * restored at launch and never continued: none of them ever says `playing`, so none of them is
 * reported. A pause and a resume, or «repeat one» going round again, are the same listen and
 * are not reported twice. And it has to be the held track's `playing`: while a nearly spent
 * link is renewed, the element still holds the track before, and a `playing` of that one is
 * not a start of this one.
 *
 * ## Two decks (task 45)
 *
 * `spec.md` «Микс и дополнения плеера», «Две деки» and «Переход». The player has two decks
 * (`engine/element.ts`) and one of them is **active**: the one whose track the slice holds — the
 * lock screen, the slider, the tonearm, the scratch and the watchdog look at it, and its events
 * are the ones this module answers. The other one's events are read for three things only: it
 * is alive, it ended, it failed. Anything else from it — its `pause` above all — is not the
 * player's.
 *
 * - **The first tap unlocks both.** The active deck gets its track; the other one, in the same
 *   tap, a tenth of a second of silence (`engine/silence.ts`) and a `play()`, and on its first
 *   sound it is paused again. Then the one audio context (`engine/sound.ts`) is made — after
 *   `audioSession` — and woken, still inside the tap. A deck that is not active and not in an
 *   overlap has no business sounding: whatever makes it sound is paused on its `playing`.
 * - **The decks go into the context only once it runs.** Element → its chain of the mixer →
 *   speaker (since task 56; a gain of its own before), for good: a deck in a context asleep is
 *   silent while it «plays». So they are routed when the context says
 *   `running`, never before; a phone that never wakes it keeps its decks as they were, and every
 *   track goes into the next as a cut.
 * - **The next track is made ready ahead.** {@link PREFETCH_LEAD_S} before the overlap, the
 *   other deck gets the next track's URL, its first second (the end of its lead-in silence,
 *   task 46) — silent, since the crossfader stands at the playing deck's side — instead of the
 *   head request of a cut (`engine/prefetch.ts`).
 * - **The overlap** (`lib/transition.ts`): at the playing track's `duration − tail − length` the
 *   other deck plays, and every {@link WRITE_EVERY_MS} — not more often — the crossfader moves
 *   towards the new deck's side by the old track's own second; the gains follow it by the
 *   equal-power curve (task 56, «The mixer and the automat»). In its middle the track switches: the
 *   other deck is the active one, the queue moves on, the lock screen and «недавние» learn of the
 *   new track (stories 105–106). At its end the old deck is paused.
 * - **The listener mid-overlap.** «Следующий» ends it at once — the new track at full volume, the
 *   old one paused, no third track (story 107); «предыдущий» ends it and then does what it always
 *   does. A pause pauses both, and the gains stand still with them, since the curve follows a
 *   deck's second; «Играть» goes on with both. A seek, a tap on another track: the overlap ends —
 *   called off before its middle, finished after it.
 * - **«Повтор одного»** goes round by an overlap of the same file on the other deck — the same
 *   listen, not reported again. The end of the queue has no overlap; `cut` is task 08's way.
 * - **When it cannot be**, it is a cut: one deck only, no context, a second deck still locked, a
 *   link of the next track about to expire, a track of unknown length. An overlap whose new deck
 *   refuses to play or fails is called off, and that track ends as a cut.
 * - **The watchdog** counts an overlap alive while either deck moves: the old one may be in its
 *   tail and the new one in its lead-in, and neither is «звук не идёт».
 *
 * ## The mix (task 47)
 *
 * `spec.md` «Микс и дополнения плеера», «Микс: темп и фаза»; the arithmetic is `lib/mix.ts`, and
 * `lib/transition.ts` says when an overlap is one. A mix is an overlap with three more things:
 *
 * - **Speeds.** Each deck's speed is its own tempo times its own factor — 1 but in a mix — and
 *   is written through its speed control like every other speed, so the tone is let go at every
 *   speed but exactly 1 and nothing is written oftener than 50 ms. Since task 55 a tempo is a
 *   deck's own, and the next deck's factor is reckoned from both («Two equal decks»). The next
 *   deck gets its factor when it is made ready.
 * - **The start on the beat.** A mix is armed {@link MIX_ARM_S} ahead — «МИКС» lit — and its own
 *   50 ms interval plays the next deck within half a step of its
 *   start. A step that comes later than that (a phone that slows a background timer) starts the
 *   next deck as far on in its file as the step is late. Until then a pause is a pause of the
 *   playing deck only: the next one has not started.
 * - **Kept together, and back.** Every step, once the next deck has sounded, the drift of the
 *   beats nudges the next deck before the middle and the old one after it; after the end the new
 *   track goes back to the tempo in thirty seconds of sound, and the lock screen learns its speed
 *   at the middle and at the end of that. A hand on the record ends the way back — let go, it
 *   comes to the tempo — and so does a track started by hand, which starts at the tempo.
 *
 * **An address puts an element's speed back to 1.** The load algorithm does it, on every new
 * `src` and `load()` (headless Chrome, 2026-10-05). Until task 47 a track started at a tempo of
 * +3 % played at 1 until the slider moved; now every deck's `loadstart` writes its own speed back.
 *
 * ## Two equal decks (task 55)
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Две равные деки». The two elements are the
 * pult's decks A and B (`store/deck.ts`), and each has its own tempo, PITCH BEND, KEYLOCK, track
 * and CUE. The player writes each deck's track, sound and second into the deck slice for the pult,
 * at every event that changes them and once a {@link TICK_MS}, and tells it which deck is active.
 *
 * - **A tempo per deck.** A deck's speed is its tempo × its factor × its bend. In a mix the next
 *   deck's factor is the playing deck's speed counted in the next deck's tempo, times the two
 *   BPMs: `BPM playing × speed playing / (BPM next × tempo next)` — the old formula when the two
 *   tempos are one.
 * - **LOAD** on the deck not active: the track's address, the end of its lead-in silence, a pause,
 *   and the track next in the queue, so the overlap goes into that deck as it stands. On the
 *   active deck: its track stops, and the new one is held at the end of its silence as the queue's
 *   current entry, as a track restored at launch is.
 * - **The listener's own deck** ({@link Deck.manual}): given a track by LOAD or started by
 *   START·STOP while not active, or left playing when the crossfader made the other deck the
 *   active one. The player does not take it for the next track, does not silence it, and its
 *   pause — «Пауза», the lock screen, the sleep timer — stops it too. How loud it is, is the
 *   mixer's.
 * - **The active deck** is the louder by the mixer (task 56). With AUTO, the listener's own deck
 *   that plays when the active track ends goes on as the active one, the crossfader to its side.
 * - **CUE** on a pause marks the second the deck is at — its music's start when it has not moved;
 *   in play it goes back to the mark and plays on. **PITCH BEND** is a factor of 1,04 or 0,96
 *   while held, and 1 at once when let go — no way back of thirty seconds. **KEYLOCK** holds the
 *   deck's tone at every speed of its own (`engine/speed.ts`).
 * - **A HOT CUE** (task 60) is the listener's own seek on a deck ({@link PlayerController.jumpDeck}):
 *   on the active deck an overlap ends, as any seek ends it. A cue is set where the deck stands
 *   ({@link PlayerController.whereDeck}) — read off its element, not off the deck slice, which is
 *   up to half a second old.
 *
 * ## The mixer and the automat (task 56)
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Цепочка звука деки, микшер, автомат». Each
 * deck's gain is the mixer's — LEVEL × fader × crossfader, as the pult's controls stand
 * (`store/pult.ts`, `lib/pult-mixer.ts`) — times the sleep timer's share; its tone and filter the
 * knobs'. Every change of a control writes them. At rest — the faders up, the knobs in the middle,
 * the crossfader at the active deck's side — the deck playing is at 1 and the other at 0.
 *
 * - **The automat is the crossfader** — and, with PRESET, the outgoing deck's effects (task 58,
 *   below). An overlap moves it from where it stood to the new deck's side, and switches the track
 *   in its middle, as before. A finger on it (`held`) takes it from the
 *   automat, and the next write after the finger lets go is the automat's again; an overlap that
 *   ends under the finger leaves the old deck playing as the listener's. A track played from a list
 *   brings the crossfader to its deck's side: it is to be heard.
 * - **The active deck follows the listener's mixer**: the louder deck by fader × crossfader, when it
 *   holds a track; the deck it leaves, still playing, becomes the listener's own. The automat's own
 *   moves do not count — it switches the active deck itself.
 * - **AUTO off** (`store/pult.ts`): the end of a track calls no next, the other deck makes nothing
 *   ready, LOAD leaves the queue alone, and «следующий» / «предыдущий» are one transition now onto
 *   the other deck ({@link PlayerController.next}, `oneShot`; `lib/transition.ts`, `planNow`).
 * - **MAIN's level** is the meter's to read ({@link PlayerController.level}); the pult reads it ten
 *   times a second while it is open — `requestAnimationFrame` stays out of this module.
 *
 * ## The effects, ECHO OUT and DUB OUT (task 58)
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Эффекты». Each deck's four effects take their
 * numbers from BEATS, DEPTH, the chosen effect and its FX paddle, and from its tempo — its track's
 * measure at the speed the deck plays (`lib/pult-effects.ts`): every change of any of them writes
 * them, as a knob writes the tone.
 *
 * - **ECHO OUT** ({@link PlayerController.echoOut}): the deck's tail opens — the last of the track,
 *   held by a delay of its own (`lib/pult-effects.ts`) — its gain goes to 0 in 50 ms (the **cut**),
 *   and after four repeats the deck pauses and the tail shuts. A pause of the deck, whoever makes
 *   it, ends the cut: the gain is the mixer's again, the tail rings on. The deck playing again, or
 *   a new track on it, ends the whole ECHO OUT — a deck started again is heard, and never stops by
 *   itself. The deck going out of a transition is the transition's end: it settles, the next track
 *   alone, and the transition never waits for a deck that will not play again.
 * - **DUB OUT** (PRESET, `lib/pult-presets.ts`): every step of an automatic transition asks the
 *   table what the deck going out is to have. The effect, BEATS and that deck's paddle are written
 *   once, where the preset begins — a paddle or a key is a tap, not a hold, and a listener who
 *   moves one after must keep it; DEPTH glides on, never under a finger; the tone, LEVEL and the
 *   faders are never the automat's. In the middle it is that deck's ECHO OUT. The paddle the
 *   preset raised goes down at the end — of the ECHO OUT, or of the transition called off or cut
 *   short: the deck is the next track's next time, and must not come in drowned.
 *
 * ## The sleep timer (task 48)
 *
 * `spec.md` stories 134–136. The slice holds when it falls — `sleepAt`, unix ms, or `'track'` for
 * the end of the track playing — and {@link TICK_MS}'s interval watches it and writes what is
 * left (`sleepLeftS`). In its last {@link SLEEP_FADE_S} seconds a 50 ms interval of its own takes
 * every deck's gain down by the square of the share left — an even fade to the ear, where a
 * straight line stays loud and then drops — on top of whatever an overlap writes, so an overlap
 * under way fades whole. On time: a pause, and the gains back to what they were, so «Играть»
 * sounds. Off — one tap — gives the gains back at once. A phone without the context cannot fade:
 * it only pauses on time.
 *
 * «До конца трека» is the end of the playing track's music (its length less the measured silence
 * at its end), at the deck's speed: no next track is made ready and no overlap starts — a pause
 * instead of a transition. A file that ends before the interval looks ends as the queue's end
 * does, and the next track does not start either. The timer is kept for this launch only.
 *
 * ## A long track goes on where it stopped (task 49)
 *
 * A track of twenty minutes or more that is started from its beginning — a tap in a list, the
 * next of the queue — starts instead where the listener stopped last time, if that is more than
 * half a minute from either end (`engine/positions.ts`). The place is known before the tap, so
 * the start is still one `src`, `currentTime`, `play()` inside it; the slice says from where
 * (`resumeFromS`), and the loading line shows it. The start also asks the server for the place
 * afresh: another phone may have moved it, or the tap came before the launch's answer. When the
 * answer names another second, the sound goes there — unless the listener has moved it by hand
 * meanwhile, or has already heard half a minute from where it started.
 *
 * ## A hand moving the sound elsewhere (task 18)
 *
 * While the scratch voice plays under a finger and hands the sound back, the element stands at
 * speed 0 where the touch began, and its `currentTime` says nothing about what is heard. The
 * voice's second is handed in through {@link PlayerController.showPosition}, and while it is,
 * every write of the slice's position writes that second instead of the element's — the
 * slider and the tonearm show the groove under the needle, not where the element waits.
 *
 * A track held at launch — restored, or opened by a link — has no element behind it yet, only
 * the second it waits at. A hand's second is shown for it too, and without a hand, that second.
 * Until 2026-09-29 it was not: a needle carried over a held track stood still until the track had
 * played once — the owner found it right after a launch («тонарм сначала тормозит») — and the
 * scratch took the element's zero for the track's second ({@link PlayerController.heldAtOf}).
 */

import logoArtworkUrl from '../assets/logo-512.png';
import { fetchStreamLink, fetchTracks } from '../lib/api';
import type { StreamLink, TrackItem } from '../lib/api';
import { nudge, phaseErrorS, returnFactor, startFromS } from '../lib/mix';
import {
  BEATS_KNOB,
  DEPTH_KNOB,
  ECHO_OUT_FADE_S,
  beatsAt,
  beatsKnob,
  deckBpm,
  deckEffects,
  echoOutTailS,
  leverOf,
} from '../lib/pult-effects';
import { presetAt } from '../lib/pult-presets';
import { SWITCH_AT, frameAt, planNow, planTransition } from '../lib/transition';
import type { TransitionPlan } from '../lib/transition';
import type { DeckSide } from '../lib/pult-layout';
import { deckGains, louderDeck, sideOfDeck } from '../lib/pult-mixer';
import type { MixerPositions, ToneBand } from '../lib/pult-mixer';
import { queryOfKey } from '../store/library';
import { setActiveDeck, tempoRate, updateDeck, useDeck } from '../store/deck';
import { chooseEffect, setValue, usePult, valueOf } from '../store/pult';
import { IDLE_PLAYER, isSounding, usePlayer } from '../store/player';
import type { PlayerProblem } from '../store/player';
import { nextIndex, passLeft, previousIndex, useQueue } from '../store/queue';
import type { QueueEntry, QueueSource } from '../store/queue';
import { useSession } from '../store/session';

import type { MediaElementLike } from './element';
import { deckA, deckB } from './element';
import type { MediaSessionLike } from './mediasession';
import {
  resolveMediaSession,
  setNowPlaying,
  setPlaybackState,
  setupMediaSession,
  syncPosition,
} from './mediasession';
import { RESUME_EDGE_S, isLongTrack, placeOf, refreshPlace, resumePoint } from './positions';
import { PREFETCH_LEAD_S, prefetchHead } from './prefetch';
import { currentPosition, effectiveDuration, seekLimit } from './readings';
import { recordPlayed } from './recent';
import type { MixerDeck } from './mixer';
import type { SoundNode } from './scratch';
import { SILENCE_URL } from './silence';
import { sound as appSound } from './sound';
import type { Sound } from './sound';
import { WRITE_EVERY_MS, createSpeed } from './speed';
import type { SpeedControl } from './speed';
import { runningSync } from './sync';
import { reloadAndPlay, safePlay, unlockWith } from './unlock';
import type { PlayOutcome } from './unlock';

/* ────────────────────────────────────────────────────────────────────────────
 * Constants
 * ──────────────────────────────────────────────────────────────────────────── */

/** How often the position is re-read and the watchdog looks. Between `timeupdate`s. */
export const TICK_MS = 500;

/** Twelve seconds without the position moving or data arriving (`spec.md` «Плеер»). */
export const STALL_AFTER_MS = 12_000;

/** A URL with less than this left is replaced before it is played. */
export const STREAM_SAFETY_S = 300;

/** «Предыдущий» past this point goes back to the start of the track, like every player. */
export const RESTART_AFTER_S = 3;

/** How often a playing track's second is written to the server. */
export const SAVE_EVERY_MS = 10_000;

/** Task 44: the next page of a queue's list is asked for with this many tracks or fewer left. */
export const FEED_AHEAD = 2;

/**
 * Task 47: a mix is armed this many seconds of the playing track before its start, so that one
 * look of the {@link TICK_MS} interval surely falls in between, and the 50 ms one starts it.
 */
export const MIX_ARM_S = 1;

/** Task 47: one step of the overlap's interval, in seconds — the precision of a mix's start. */
const STEP_S = WRITE_EVERY_MS / 1000;

/** Task 48: the sleep timer fades the sound out over its last this many seconds. */
export const SLEEP_FADE_S = 10;

/** Task 48: what the sleep timer can be set to — minutes, or the end of the track playing. */
export type SleepChoice = 15 | 30 | 60 | 'track';

/**
 * Shown on the lock screen when a track has no cover: the house logo, as the list shows it —
 * as a 512-pixel PNG render of `assets/logo.svg`, not the SVG itself, which a lock screen is
 * not bound to draw (task 19).
 */
export const LOGO_ARTWORK: string = logoArtworkUrl;

/** The element events listened to. Deliberately not `visibilitychange`: see the header. */
const MEDIA_EVENTS = [
  'loadstart',
  'loadedmetadata',
  'durationchange',
  'timeupdate',
  'progress',
  'waiting',
  'playing',
  'pause',
  'ended',
  'seeked',
  'error',
] as const;

/** Task 55: the events after which a deck's state is written for the pult at once. */
const PUBLISHED: ReadonlySet<string> = new Set([
  'loadstart',
  'playing',
  'pause',
  'ended',
  'seeked',
]);

/* ────────────────────────────────────────────────────────────────────────────
 * The seam
 * ──────────────────────────────────────────────────────────────────────────── */

/** A fresh link, `'gone'` for a track that left the channel, `undefined` for «could not ask». */
export type FreshStream = (trackId: number) => Promise<StreamLink | 'gone' | undefined>;

/** Task 44: a page of a list, and the label of the one after it; `null` — the list is over. */
export interface ListPage {
  readonly items: readonly TrackItem[];
  readonly nextCursor: string | null;
}

/** Task 44: the page of a list after `source.cursor`, or `undefined` for «could not get it». */
export type NextPage = (source: QueueSource) => Promise<ListPage | undefined>;

export interface PlayerDeps {
  /** Deck A — the only deck of a player built without {@link PlayerDeps.second}. */
  readonly el: MediaElementLike;
  /** Task 45: deck B. Absent — one deck, and every track goes into the next as a cut. */
  readonly second?: MediaElementLike | undefined;
  /** Task 45: the context the decks' gains live in. Absent — no gains, and every transition a cut. */
  readonly sound?: Sound | undefined;
  /** `null` on every Android phone. */
  readonly session: MediaSessionLike | null;
  readonly freshStream: FreshStream;
  /** Milliseconds. */
  readonly now: () => number;
  /** Asks for the state to be written to the server now. Must not wait, must not throw. */
  readonly persist: () => void;
  /** Asks for the head of `url` ahead of time. Decides itself whether the connection allows it. */
  readonly prefetch: (url: string) => void;
  /** Task 11: `track` has really started — see the header. Must not wait, must not throw. */
  readonly played: (track: TrackItem) => void;
  /** Task 44: the next page of the queue's list. Absent — the queue never grows by itself. */
  readonly nextPage?: NextPage | undefined;
}

/** Task 60: a deck as a HOT CUE sees it — see {@link PlayerController.whereDeck}. */
export interface DeckWhere {
  readonly trackId: number;
  readonly positionS: number;
  readonly sounding: boolean;
}

export interface PlayerController {
  /**
   * Builds the queue from `list`, from `track` to the end, and plays `track`. Call it from the
   * tap itself. Task 44: `source` is that list's next page, when it has one.
   */
  playFrom(list: readonly TrackItem[], track: TrackItem, source?: QueueSource | null): void;
  /** Task 44: «Играть следующим» — `track` right after the one playing. `false` when full. */
  playNext(track: TrackItem): boolean;
  /** Plays the queue's entry at `index`. From the tap itself. */
  playAt(index: number): void;
  /** Appends `track` to the queue; with nothing held, holds it. `false` when the queue is full. */
  enqueue(track: TrackItem): boolean;
  /**
   * Holds the queue's current track at `positionS` without a sound — the restored state of a
   * launch. Does nothing if the player already holds a track.
   */
  cue(positionS: number): void;
  /** Play or pause, from a button. */
  toggle(): void;
  pause(): void;
  /** Seconds. Clamped to what may be sought. */
  seek(positionS: number): void;
  next(): void;
  previous(): void;
  /** «Повторить» and «Играть». */
  retry(): void;
  /** Hands the state to the server for the next launch. Touches nothing of the sound. */
  save(): void;
  /** Re-reads the element into the store. Touches nothing of the sound. */
  resync(): void;
  /** Task 09: the active deck's speed under a finger on the record (`engine/speed.ts`). */
  readonly speed: Pick<
    SpeedControl,
    'touch' | 'hush' | 'set' | 'release' | 'releaseNow' | 'base' | 'touched'
  >;
  /**
   * Task 18: the second the screen shows while a hand plays the sound elsewhere — see the
   * header. `undefined` gives the position back to the element. Touches nothing of the sound.
   */
  showPosition(positionS: number | undefined): void;
  /** Task 48: the sleep timer on, or off with `undefined`. From a button. */
  setSleep(choice: SleepChoice | undefined): void;
  /**
   * Task 55: LOAD — `track` on deck `side`, paused at the end of its lead-in silence, and next in
   * the queue — see «Two equal decks». From the tap itself.
   */
  loadDeck(side: DeckSide, track: TrackItem): void;
  /** Task 55: START·STOP of deck `side`. From the tap itself. */
  toggleDeck(side: DeckSide): void;
  /** Task 55: CUE of deck `side`: a mark on a pause, back to it in play. */
  cueDeck(side: DeckSide): void;
  /**
   * Task 60: a HOT CUE of deck `side` — to `positionS` as the listener's own seek: on the active
   * deck an overlap ends, as a seek ends it; a deck that plays plays on, a pause stays a pause.
   */
  jumpDeck(side: DeckSide, positionS: number): void;
  /**
   * Task 60: deck `side` now — its track, its second read off the element (the slice's is up to
   * half a second old) and whether it sounds; `undefined` with no track. For a HOT CUE set.
   */
  whereDeck(side: DeckSide): DeckWhere | undefined;
  /** Task 55: PITCH BEND of deck `side` — `1` faster, `-1` slower while held, `0` let go. */
  bendDeck(side: DeckSide, direction: -1 | 0 | 1): void;
  /** Task 56: MAIN's level now, 0…1 — for the meter, read while the pult is open. */
  level(): number;
  /** Task 58: ECHO OUT of deck `side` — see «The effects, ECHO OUT and DUB OUT». From the tap. */
  echoOut(side: DeckSide): void;
  /** Task 57: the deck that is active now. */
  activeSide(): DeckSide;
  /** Task 57: deck `side`'s element — the scratch takes the sound from it and gives it back. */
  elementOf(side: DeckSide): MediaElementLike | undefined;
  /** Task 57: deck `side`'s speed under a hand — the active deck's is {@link speed}. */
  speedOf(side: DeckSide): PlayerController['speed'] | undefined;
  /**
   * Task 57: deck `side` to `positionS`, where the scratch hands the sound back. Not {@link seek}:
   * a hand-over is not the listener's seek, and an overlap goes on through it.
   */
  seekDeck(side: DeckSide, positionS: number): void;
  /**
   * Task 18: the URL deck `side` plays its track from — for the active deck the catalogue's, or
   * the fresh one the player took for a nearly spent link. `undefined` with no track.
   */
  soundUrlOf(side: DeckSide): string | undefined;
  /**
   * The second a track held at launch (`cue`) waits at before its first play; `undefined` once
   * the element has the track. A hand starts from here, not from the element's zero. Only the
   * active deck holds a track so.
   */
  heldAtOf(side: DeckSide): number | undefined;
  /**
   * Task 57: the door of deck `side`'s chain (`engine/mixer.ts`) — the scratch voice of the deck
   * sounds through it; `undefined` while the decks are not in the context.
   */
  headOf(side: DeckSide): SoundNode | undefined;
  dispose(): void;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The controller
 * ──────────────────────────────────────────────────────────────────────────── */

/** Task 44: the same list, at the same page. */
function sameSource(a: QueueSource, b: QueueSource | null): boolean {
  return b !== null && a.key === b.key && a.cursor === b.cursor;
}

/** Task 45: one deck — its element, its speed, its gain once routed. */
interface Deck {
  /** Task 55: the pult's deck it is — the first element A, the other B. */
  readonly side: DeckSide;
  readonly el: MediaElementLike;
  readonly speed: SpeedControl;
  /** Since task 56 its chain of the pult's mixer (`engine/mixer.ts`). */
  gain: MixerDeck | undefined;
  /** The gain last written, so a step that changes nothing writes nothing. */
  lastGain: number | undefined;
  /** The deck has sounded once: a `play()` went through, and it may play without a tap now. */
  unlocked: boolean;
  /** Task 47: its speed as a share of the deck's tempo — 1 but in a mix and the way back from it. */
  factor: number;
  /** Task 55: PITCH BEND — 1, or 1,04 / 0,96 while held. */
  bend: number;
  /** Task 55: the track the deck holds, whoever gave it. */
  track: TrackItem | undefined;
  /** Task 55: the queue entry of that track, when it has one. */
  entryKey: number | undefined;
  /**
   * Task 55: the listener's own deck — given a track by LOAD or started by START·STOP while not the
   * active one. The player does not take it for the next track, and does not pause its sound.
   */
  manual: boolean;
  /** Task 58: the deck is going out in its echo — until its pause. */
  echoOut: EchoOut | undefined;
}

/** Task 58: an ECHO OUT under way. */
interface EchoOut {
  /** The deck's pause and the tail's end, once it has repeated four times. */
  readonly timer: ReturnType<typeof setTimeout>;
  /** The deck's gain is held at 0 — until the deck pauses. */
  cut: boolean;
  /** DUB OUT raised the deck's paddle: it goes back down at the end. */
  byPreset: boolean;
}

/** Task 45: the next track, ready on the deck that does not play. */
interface Ready {
  readonly deck: Deck;
  /** The queue entry, by its key: the queue may be edited meanwhile. */
  readonly key: number;
  readonly track: TrackItem;
  /** The generation of the track playing when it was made ready. */
  readonly forGeneration: number;
}

/** Task 45: one track going into the next. */
interface Overlap {
  readonly ready: Ready;
  /** The deck going out. Active until the middle, then the other one. */
  readonly from: Deck;
  readonly plan: TransitionPlan;
  readonly timer: ReturnType<typeof setInterval>;
  switched: boolean;
  /** Task 47: the new deck was told to play — a mix is armed a moment ahead of that. */
  started: boolean;
  /** Task 47: the new deck has sounded since: its second means something. */
  heard: boolean;
  /** Task 47: the old deck's factor when the overlap began. */
  readonly fromFactor: number;
  /** Task 55: …and its speed counted in the new deck's tempo — see «Two equal decks». */
  readonly fromShare: number;
  /** Task 56: where the crossfader stood when the overlap began… */
  readonly crossFrom: number;
  /** …and the new deck's side, where it ends. */
  readonly crossTo: number;
  /** Task 56: a one-shot transition with AUTO off — not the queue's next track. */
  readonly oneShot: boolean;
  /**
   * Task 58: DUB OUT — DEPTH where it stood when the preset began (`undefined`: not yet begun),
   * whether it raised the outgoing deck's paddle, and the ECHO OUT done.
   */
  readonly dub: { depthFrom: number | undefined; raised: boolean; echoedOut: boolean };
}

/** Task 47: the new track of a mix on its way back to the deck's tempo. */
interface Homing {
  readonly deck: Deck;
  readonly from: number;
  readonly timer: ReturnType<typeof setInterval>;
  /** Milliseconds of sound since the overlap ended — a pause does not count. */
  elapsedMs: number;
  lastAt: number;
}

function newDeck(side: DeckSide, el: MediaElementLike): Deck {
  return {
    side,
    el,
    speed: createSpeed(el),
    gain: undefined,
    lastGain: undefined,
    unlocked: false,
    factor: 1,
    bend: 1,
    track: undefined,
    entryKey: undefined,
    manual: false,
    echoOut: undefined,
  };
}

/** Task 55: PITCH BEND's speed while held, either way. */
export const BEND_UP = 1.04;
export const BEND_DOWN = 0.96;

/** Task 55: where a track's music starts: the end of its lead-in silence (task 46). */
function leadOf(track: TrackItem): number {
  return track.leadInS !== undefined && track.leadInS > 0 ? track.leadInS : 0;
}

export function createPlayer(deps: PlayerDeps): PlayerController {
  const { session, now } = deps;
  const first = newDeck('A', deps.el);
  const decks: readonly Deck[] =
    deps.second === undefined ? [first] : [first, newDeck('B', deps.second)];
  /** Task 45: the deck whose track the slice holds — see «Two decks». */
  let active = first;
  /** Task 45: the next track, ready on the other deck. */
  let ready: Ready | undefined;
  /** Task 45: the overlap under way. */
  let overlap: Overlap | undefined;
  /** Task 45: the generation whose overlap failed: that track goes into the next as a cut. */
  let noOverlapFor = -1;
  /** Task 47: the way back to the tempo after a mix. */
  let homing: Homing | undefined;
  /** Task 48: the share of every gain the sleep timer leaves — 1 but in its last seconds. */
  let sleepShare = 1;
  /** Task 48: the fade's own 50 ms interval, while it goes. */
  let fading: ReturnType<typeof setInterval> | undefined;
  /** Task 56: the automat is moving the crossfader now — not the listener. */
  let steering = false;

  /** The listener asked for sound and has not asked to stop. The watchdog guards only this. */
  let wantsSound = false;
  let lastAliveAt = now();
  /** One silent refresh of an expired URL per track; a second failure is the listener's. */
  let refreshedAfterError = false;
  let link: StreamLink | undefined;
  /** Bumped per track, so a slow fetch for a track left behind does not play it. */
  let generation = 0;
  /** When the request for a fresh URL went out, while it has not come back. */
  let fetchingSince: number | undefined;
  /**
   * The second a held track starts at, while the element has not been given it yet: a cued
   * track (a restored launch, a track added to an empty queue). Cleared by its first play.
   */
  let cuedAtS: number | undefined;
  /** The generation whose next track has been asked for, so it is asked for once. */
  let prefetchedFor = -1;
  /** The held track has said `playing` since it was loaded or held: it is reported once. */
  let reported = false;
  /** The generation the element was last given a URL for: its events are about that track. */
  let sourcedFor = -1;
  /** Task 18: the second a hand's sound is at, shown instead of the element's. */
  let shownS: number | undefined;
  /** Task 44: the label of the page being asked for, while it is. */
  let feeding: string | undefined;
  /** Task 49: the listener moved the sound by hand since the track was loaded. */
  let seekedByHand = false;
  /** Task 44: a page that came while the shuffled pass still had tracks ahead — see the header. */
  let held: { readonly source: QueueSource; readonly page: ListPage } | undefined;
  let lastPersistAt = now();

  const state = usePlayer.getState;
  const set = usePlayer.setState;
  /** The active deck's speed under a hand. Since task 55 each deck has its own tempo. */
  const speed: PlayerController['speed'] = {
    // Task 47: a hand takes the speed from the way back of a mix; let go, it comes to the tempo.
    touch: () => {
      active.speed.touch();
      stopHoming(true);
    },
    hush: () => {
      active.speed.hush();
      stopHoming(true);
    },
    set: (rate) => {
      active.speed.set(rate);
    },
    release: () => {
      active.speed.release();
    },
    releaseNow: () => {
      active.speed.releaseNow();
    },
    get base(): number {
      return active.speed.base;
    },
    get touched(): boolean {
      return active.speed.touched;
    },
  };
  /** The active deck's element. */
  const cur = (): MediaElementLike => active.el;
  const other = (): Deck | undefined => decks.find((deck) => deck !== active);

  const nowS = (): number => Math.floor(now() / 1000);
  const serverDuration = (): number => state().track?.durationS ?? 0;
  /** The second the screen shows: a hand's, while there is one, else the element's. */
  const shownPosition = (): number => shownS ?? currentPosition(cur(), serverDuration());

  /**
   * The numbers the screen shows, read from the element. Not for a held track: the element
   * has never been given it, and its zero would overwrite the second the track is held at.
   */
  function readNumbers(): void {
    if (state().track === undefined || cuedAtS !== undefined) return;
    const durationS = effectiveDuration(cur(), serverDuration());
    set({
      positionS: shownPosition(),
      durationS,
      seekLimitS: seekLimit(cur(), serverDuration()),
    });
  }

  function publishPosition(): void {
    syncPosition(
      session,
      effectiveDuration(cur(), serverDuration()),
      currentPosition(cur(), serverDuration()),
      speed.base,
    );
  }

  /** The player stops trying and asks the listener; `problem` says what to ask. */
  function giveUp(problem: PlayerProblem): void {
    wantsSound = false;
    set({ status: 'paused', buffering: false, problem });
  }

  function afterPlay(outcome: PlayOutcome, forGeneration: number): void {
    if (forGeneration !== generation || outcome.ok || outcome.reason === 'aborted') return;
    // `unsupported` that survived `load()` and a second try, or something unknown: the file
    // is the problem, and the listener decides whether to try again.
    giveUp(outcome.reason === 'gesture' ? 'gesture' : 'failed');
  }

  /** Starts the element on `url` at `startS`. Synchronous up to `play()`: tap-safe. */
  function start(url: string, startS: number): void {
    const forGeneration = generation;
    const el = cur();
    sourcedFor = generation;
    wantsSound = true;
    lastAliveAt = now();
    set({ status: 'loading', buffering: true, problem: undefined });
    void unlockWith(el, url, startS).then(async (outcome) => {
      // `NotSupportedError`: the element latched an error, and only `load()` clears it.
      const final =
        !outcome.ok && outcome.reason === 'unsupported' ? await reloadAndPlay(el) : outcome;
      afterPlay(final, forGeneration);
    });
  }

  async function withFreshLink(
    trackId: number,
    forGeneration: number,
  ): Promise<StreamLink | undefined> {
    fetchingSince = now();
    const fresh = await deps.freshStream(trackId);
    if (forGeneration !== generation) return undefined;
    fetchingSince = undefined;
    if (fresh === 'gone' || fresh === undefined) {
      giveUp(fresh === 'gone' ? 'gone' : 'failed');
      return undefined;
    }
    link = fresh;
    return fresh;
  }

  /** A fresh URL, then the element at `positionS`: a nearly spent link, a retry, an expiry. */
  function restartFresh(trackId: number, positionS: number, forGeneration: number): void {
    void withFreshLink(trackId, forGeneration).then((fresh) => {
      if (fresh !== undefined) start(fresh.url, positionS);
    });
  }

  function announce(track: TrackItem): void {
    setNowPlaying(session, {
      title: track.title,
      artist: track.performer,
      artwork: new URL(track.cover ?? LOGO_ARTWORK, location.href).href,
    });
  }

  /* ── the element's events ────────────────────────────────────────────── */

  function onEvent(event: Event): void {
    if (state().track === undefined) return;
    switch (event.type) {
      case 'loadedmetadata':
      case 'durationchange':
        readNumbers();
        publishPosition();
        return;
      case 'timeupdate':
        lastAliveAt = now();
        set({ positionS: shownPosition() });
        return;
      case 'progress':
        lastAliveAt = now();
        set({ seekLimitS: seekLimit(cur(), serverDuration()) });
        return;
      case 'waiting':
        set({ buffering: true });
        return;
      case 'playing': {
        lastAliveAt = now();
        refreshedAfterError = false;
        set({ status: 'playing', buffering: false, problem: undefined });
        readNumbers();
        setPlaybackState(session, 'playing');
        publishPosition();
        const heard = state().track;
        if (!reported && sourcedFor === generation && heard !== undefined) {
          reported = true;
          deps.played(heard);
        }
        return;
      }
      case 'pause':
        if (state().status === 'ended') return;
        set({ status: 'paused', buffering: false });
        setPlaybackState(session, 'paused');
        publishPosition();
        // At the end of a track the element pauses first and then ends; `ended` decides what
        // is saved, and a save from here would carry the last second of the finished track.
        if (!cur().ended) persist();
        return;
      case 'ended':
        onEnded();
        return;
      case 'seeked':
        readNumbers();
        publishPosition();
        return;
      case 'error':
        onElementError();
        return;
      default:
        return;
    }
  }

  /** The end of a track: the next one of the queue, the same one again, or silence. */
  function onEnded(): void {
    // Task 55: the listener's other deck already plays — with AUTO it goes on as the one heard.
    if (isAuto() && cutToPlaying()) return;
    if (overlap !== undefined) {
      // The old track ran out before the overlap's middle (a duration longer than the file):
      // the new one simply takes over. After the middle, it is the new one that ended.
      const wasSwitched = overlap.switched;
      settle();
      if (!wasSwitched) return;
    }
    wantsSound = false;
    const queue = useQueue.getState();
    // Task 48: the track the sleep timer waited for ran out before the interval looked.
    const asleep = state().sleepAt === 'track';
    if (asleep) {
      set({ sleepAt: undefined, sleepLeftS: undefined });
      wake();
    }
    // Task 56: AUTO off — the deck stands at its end, nothing comes next by itself.
    const next = asleep || !isAuto() ? -1 : nextIndex(queue, 'auto');
    if (next === -1) {
      set({ status: 'ended', buffering: false });
      setPlaybackState(session, 'paused');
      persist();
      return;
    }
    if (next === queue.index) {
      // «Repeat one»: the same file from its start, on the element that already holds it.
      cur().currentTime = 0;
      const forGeneration = generation;
      wantsSound = true;
      lastAliveAt = now();
      set({ status: 'loading', positionS: 0, problem: undefined });
      void safePlay(cur()).then((outcome) => {
        afterPlay(outcome, forGeneration);
      });
      return;
    }
    go(next);
  }

  function persist(): void {
    lastPersistAt = now();
    deps.persist();
  }

  /**
   * Asks for the head of the track that plays next, once per track, when this one has
   * {@link PREFETCH_LEAD_S} seconds or less to go. Not for «repeat one» (the file is the one
   * playing), not at the end of the queue, not for a link that would be replaced before use.
   */
  function prefetchNext(current: TrackItem): void {
    if (prefetchedFor === generation || cur().paused) return;
    const durationS = effectiveDuration(cur(), serverDuration());
    if (durationS <= 0 || durationS - currentPosition(cur(), serverDuration()) > PREFETCH_LEAD_S) {
      return;
    }
    prefetchedFor = generation;
    const queue = useQueue.getState();
    const next = nextIndex(queue, 'auto');
    const target = next === -1 || next === queue.index ? undefined : queue.entries[next]?.track;
    if (target === undefined || target.id === current.id) return;
    if (target.stream.expiresAt - STREAM_SAFETY_S <= nowS()) return;
    deps.prefetch(target.stream.url);
  }

  function onElementError(): void {
    const track = state().track;
    if (track === undefined) return;
    const expired = link === undefined || link.expiresAt <= nowS();
    if (expired && !refreshedAfterError) {
      // The URL simply ran out of its six hours: fetch another and carry on from here.
      refreshedAfterError = true;
      restartFresh(track.id, state().positionS, generation);
      return;
    }
    giveUp('failed');
  }

  /* ── two decks (task 45) ─────────────────────────────────────────────── */

  /** Task 55: a deck's own tempo as a speed, `1 + tempo` (task 23). */
  function tempoOf(deck: Deck): number {
    return tempoRate(useDeck.getState().decks[deck.side].pitch);
  }

  /**
   * Task 47: `deck` at `factor` of its tempo — through its speed, within the 50 ms rule — and,
   * since task 55, times its PITCH BEND.
   */
  function drive(deck: Deck, factor: number): void {
    deck.factor = factor;
    deck.speed.setBase(tempoOf(deck) * factor * deck.bend);
    // Task 58: BEATS are beats of this speed.
    applyEffects(deck);
  }

  /**
   * Writes `value` into `deck`'s gain, times the sleep timer's share — nothing when it has none or
   * the value is the same.
   */
  function writeGain(deck: Deck, value: number): void {
    // Task 58: a deck going out in its echo is silent but for the echo, 0 in 50 ms.
    const out = deck.echoOut?.cut === true;
    const written = out ? 0 : value * sleepShare;
    if (deck.gain === undefined || deck.lastGain === written) return;
    deck.lastGain = written;
    // A glide's time constant: 95 % of the way in three of them.
    deck.gain.gain(written, out ? ECHO_OUT_FADE_S / 3 : undefined);
  }

  /* ── the mixer (task 56) ─────────────────────────────────────────────── */

  /** Where the pult's mixer stands now (`store/pult.ts`). */
  function positions(): MixerPositions {
    const { values } = usePult.getState();
    return {
      level: { A: valueOf(values, 'A.level'), B: valueOf(values, 'B.level') },
      fader: { A: valueOf(values, 'A.fader'), B: valueOf(values, 'B.fader') },
      crossfader: valueOf(values, 'xfader'),
    };
  }

  /** Every deck's gain from the mixer: LEVEL × fader × crossfader — times the sleep timer's share. */
  function mixGains(): void {
    const gains = deckGains(positions());
    for (const deck of decks) writeGain(deck, gains[deck.side]);
  }

  /** A deck's tone and filter from its knobs. */
  function applyTone(deck: Deck): void {
    const chain = deck.gain;
    if (chain === undefined) return;
    const { values } = usePult.getState();
    for (const band of ['low', 'mid', 'high'] as const satisfies readonly ToneBand[]) {
      chain.tone(band, valueOf(values, `${deck.side}.${band}`));
    }
    chain.filter(valueOf(values, `${deck.side}.filter`));
  }

  /**
   * The automat moves the crossfader to `position` — unless a finger holds it: then the finger's
   * is the crossfader, and the next write after it lets go is the automat's again.
   */
  function moveCrossfader(position: number): void {
    automatSets('xfader', position);
  }

  /** The automat moves control `id` to `value` — unless a finger holds it. */
  function automatSets(id: string, value: number): void {
    if (usePult.getState().held.has(id)) return;
    // The automat's own move: the active deck is its to switch (`switchTo`), not the mixer's.
    steering = true;
    try {
      setValue(id, value);
    } finally {
      steering = false;
    }
  }

  /** Task 58: a deck's tempo in beats a minute, at the speed it plays. */
  function bpmOf(deck: Deck): number {
    return deckBpm(deck.track?.beat, tempoOf(deck) * deck.factor * deck.bend);
  }

  /** Task 58: a deck's four effects from the pult and its tempo. */
  function applyEffects(deck: Deck): void {
    const chain = deck.gain;
    if (chain === undefined) return;
    const { values, effect } = usePult.getState();
    chain.effects(
      deckEffects({
        effect,
        lever: valueOf(values, leverOf(deck.side)),
        beats: valueOf(values, BEATS_KNOB),
        depth: valueOf(values, DEPTH_KNOB),
        bpm: bpmOf(deck),
        tail: deck.echoOut !== undefined,
      }),
    );
  }

  /** Task 58: ECHO OUT of deck `side` — see «The effects, ECHO OUT and DUB OUT». */
  function echoOut(side: DeckSide, byPreset = false): void {
    const deck = deckOf(side);
    if (deck?.gain === undefined || deck.el.paused || deck.echoOut !== undefined) return;
    const beats = beatsAt(valueOf(usePult.getState().values, BEATS_KNOB));
    deck.echoOut = {
      cut: true,
      byPreset,
      timer: setTimeout(
        () => {
          stopForEchoOut(deck);
        },
        echoOutTailS(beats, bpmOf(deck)) * 1000,
      ),
    };
    // The tail opens first, then the gain goes: what played last is in it (`engine/mixer.ts`).
    applyEffects(deck);
    mixGains();
  }

  /** The tail has rung four times: the deck stops, as START·STOP stops it, and the tail shuts. */
  function stopForEchoOut(deck: Deck): void {
    // The deck going out of a transition: the transition is over — the next track alone.
    if (overlap?.from === deck) settle();
    if (!deck.el.paused) {
      if (deck === active) wantsSound = false;
      deck.el.pause();
      publishDeck(deck);
    }
    endEchoOut(deck);
  }

  /** The deck paused: the cut is over, the gain is the mixer's again; the tail rings on. */
  function endCut(deck: Deck): void {
    if (deck.echoOut?.cut !== true) return;
    deck.echoOut.cut = false;
    mixGains();
  }

  /** The tail rang out, the deck plays again, or took another track: ECHO OUT is over. */
  function endEchoOut(deck: Deck): void {
    const out = deck.echoOut;
    if (out === undefined) return;
    clearTimeout(out.timer);
    deck.echoOut = undefined;
    applyEffects(deck);
    mixGains();
    if (out.byPreset) automatSets(leverOf(deck.side), 0);
  }

  /**
   * Task 58: DUB OUT, one step of an automatic transition — what PRESET's table says the deck going
   * out is to have `positionS` into its track. Nothing with PRESET `clean`.
   */
  function dubOut(going: Overlap, positionS: number): void {
    const { preset, values } = usePult.getState();
    const middleS = going.plan.startAtS + going.plan.lengthS * SWITCH_AT;
    // The plan is in the outgoing track's seconds: its beats at its own measure, speed aside.
    const beatS = 60 / deckBpm(going.from.track?.beat, 1);
    const begun = going.dub.depthFrom !== undefined;
    const depthFrom = going.dub.depthFrom ?? valueOf(values, DEPTH_KNOB);
    const step = presetAt(preset, (middleS - positionS) / beatS, depthFrom);
    if (step === undefined) return;
    going.dub.depthFrom = depthFrom;
    const side = going.from.side;
    // Once, where the preset begins: a key or a paddle moved after is the listener's.
    if (!begun) {
      if (step.effect !== undefined) chooseEffect(step.effect);
      if (step.beats !== undefined) automatSets(BEATS_KNOB, beatsKnob(step.beats));
      if (step.lever !== undefined && valueOf(values, leverOf(side)) !== step.lever) {
        automatSets(leverOf(side), step.lever);
        going.dub.raised = true;
      }
    }
    if (step.depth !== undefined) automatSets(DEPTH_KNOB, step.depth);
    if (step.echoOut && !going.dub.echoedOut) {
      going.dub.echoedOut = true;
      echoOut(side, going.dub.raised);
      // The listener's own ECHO OUT under way: it lowers the paddle the preset raised.
      if (going.from.echoOut !== undefined && going.dub.raised) going.from.echoOut.byPreset = true;
    }
  }

  /**
   * Task 58: the transition is over — finished, called off or cut short: the paddle DUB OUT raised
   * goes down, unless its ECHO OUT still rings and lowers it at its end.
   */
  function endDub(going: Overlap): void {
    if (!going.dub.raised || going.from.echoOut?.byPreset === true) return;
    automatSets(leverOf(going.from.side), 0);
  }

  /** AUTO — see «The mixer and the automat». */
  function isAuto(): boolean {
    return usePult.getState().auto;
  }

  /**
   * The listener moved a fader or the crossfader: the deck the mixer makes the louder is the active
   * one, when it holds a track — the active deck changes where the crossfader crosses the middle, as
   * in the middle of an overlap. An overlap under way switches by itself.
   */
  function followMixer(): void {
    if (overlap !== undefined) return;
    const louder = louderDeck(positions());
    if (louder === undefined || louder === active.side) return;
    const deck = deckOf(louder);
    if (deck?.track === undefined) return;
    const before = active;
    const playingOn = !before.el.paused;
    becomeActive(deck, deck.track, deck.entryKey);
    // The deck the crossfader left, still playing, is the listener's own now: the automat does
    // not take it for the next track, the player's pause stops it, nothing silences it.
    if (playingOn) before.manual = true;
  }

  /* ── the sleep timer (task 48) ───────────────────────────────────────── */

  /** Seconds to the sleep timer, or `undefined` — off, or the end of a track of unknown length. */
  function sleepLeft(): number | undefined {
    const at = state().sleepAt;
    if (at === undefined) return undefined;
    if (at !== 'track') return Math.max(0, (at - now()) / 1000);
    const track = state().track;
    const durationS = effectiveDuration(cur(), serverDuration());
    if (track === undefined || !(durationS > 0)) return undefined;
    const tailS = track.tailS !== undefined && track.tailS > 0 ? track.tailS : 0;
    const positionS = cuedAtS ?? currentPosition(cur(), serverDuration());
    return Math.max(0, (durationS - tailS - positionS) / speed.base);
  }

  /** Once a tick: what is left into the slice, the fade begun, or the pause on time. */
  function watchSleep(): void {
    const left = sleepLeft();
    if (left === undefined) return;
    const shown = Math.ceil(left);
    if (state().sleepLeftS !== shown) set({ sleepLeftS: shown });
    if (left <= 0) {
      fallAsleep();
      return;
    }
    if (left <= SLEEP_FADE_S && fading === undefined) {
      fading = setInterval(fade, WRITE_EVERY_MS);
      fade();
    }
  }

  /** One step of the fade: every gain at the square of the share of the fade left. */
  function fade(): void {
    const left = sleepLeft();
    if (left === undefined) {
      wake();
      return;
    }
    if (left <= 0) {
      fallAsleep();
      return;
    }
    sleepShare = Math.min(1, left / SLEEP_FADE_S) ** 2;
    mixGains();
  }

  /** The fade stopped and every gain back at what it is to be. */
  function wake(): void {
    if (fading !== undefined) clearInterval(fading);
    fading = undefined;
    sleepShare = 1;
    mixGains();
  }

  /** The timer falls: a pause, the gains back, the timer off. */
  function fallAsleep(): void {
    set({ sleepAt: undefined, sleepLeftS: undefined });
    if (isSounding(state().status)) pause();
    wake();
  }

  function setSleep(choice: SleepChoice | undefined): void {
    wake();
    if (choice === undefined) {
      set({ sleepAt: undefined, sleepLeftS: undefined });
      return;
    }
    // A mix armed and not yet started is called off: the track is to end, not to go on.
    if (choice === 'track' && overlap?.started === false) abandon();
    set({ sleepAt: choice === 'track' ? 'track' : now() + choice * 60_000 });
    const left = sleepLeft();
    set({ sleepLeftS: left === undefined ? undefined : Math.ceil(left) });
  }

  /** The queue entry that plays when this track ends by itself — itself under «повтор одного». */
  function upcoming(): { readonly index: number; readonly entry: QueueEntry } | undefined {
    const queue = useQueue.getState();
    const index = nextIndex(queue, 'auto');
    const entry = index === -1 ? undefined : queue.entries[index];
    return entry === undefined ? undefined : { index, entry };
  }

  /** Both decks unlocked and routed, and this track's overlap has not failed. */
  function mayOverlap(): boolean {
    return (
      decks.length > 1 &&
      decks.every((deck) => deck.unlocked && deck.gain !== undefined) &&
      noOverlapFor !== generation
    );
  }

  /** Task 55: the active deck's tempo over the other one's: 1 while the two tempos are one. */
  function relativeTempo(): number {
    const next = other();
    return next === undefined ? 1 : tempoOf(active) / tempoOf(next);
  }

  /** The overlap from `current` into `next`, or `undefined` for a cut — see «Two decks». */
  function overlapPlan(current: TrackItem, next: TrackItem): TransitionPlan | undefined {
    if (!mayOverlap() || next.stream.expiresAt - STREAM_SAFETY_S <= nowS()) return undefined;
    return planTransition(useQueue.getState().transition, {
      durationS: effectiveDuration(cur(), serverDuration()),
      tailS: current.tailS,
      nextLeadInS: next.leadInS,
      nextDurationS: next.durationS,
      nextTailS: next.tailS,
      leadInS: current.leadInS,
      beat: current.beat,
      nextBeat: next.beat,
      // Task 55: the playing deck's speed in the next deck's tempo — see «Two equal decks».
      factor: active.factor * relativeTempo(),
    });
  }

  /**
   * The next track on the other deck, at its first second, at no volume — and, in a mix, at the
   * playing one's tempo: after the address, which sets an element's speed back to 1.
   */
  function makeReady(entry: QueueEntry, plan: TransitionPlan): void {
    const deck = other();
    if (deck === undefined) return;
    if (!deck.el.paused) deck.el.pause();
    deck.el.src = entry.track.stream.url;
    if (plan.nextFromS > 0) deck.el.currentTime = plan.nextFromS;
    drive(deck, plan.mix?.factor ?? 1);
    ready = { deck, key: entry.key, track: entry.track, forGeneration: generation };
    holds(deck, entry.track, entry.key, false);
  }

  /**
   * Once a tick: the head of the next track for a cut, or for an overlap the next track made
   * ready {@link PREFETCH_LEAD_S} ahead and the overlap begun at its second.
   */
  function lookAhead(current: TrackItem): void {
    if (overlap !== undefined) return;
    // Task 56: AUTO off — the other deck makes nothing ready, nothing comes by itself.
    if (!isAuto()) return;
    // Task 48: the sleep timer waits for the end of this track — nothing comes after it.
    if (state().sleepAt === 'track') {
      ready = undefined;
      return;
    }
    const next = upcoming();
    // Task 55: the listener's own deck is not taken for the next track while it plays, unless it
    // holds that track. Silent, and holding another, it is the automat's again (AUTO, task 56).
    const spare = other();
    const playingOwn = spare?.manual === true && !spare.el.paused;
    const taken = playingOwn && spare.entryKey !== next?.entry.key;
    if (spare?.manual === true && !playingOwn && spare.entryKey !== next?.entry.key) {
      spare.manual = false;
    }
    const plan = next === undefined || taken ? undefined : overlapPlan(current, next.entry.track);
    if (next === undefined || plan === undefined) {
      if (!taken) ready = undefined;
      prefetchNext(current);
      return;
    }
    // A deck the listener has already started is not faded in from nothing: it plays as it is,
    // and takes over when this track ends (`onEnded`).
    if (playingOwn) return;
    const positionS = currentPosition(cur(), serverDuration());
    if (positionS < plan.startAtS - PREFETCH_LEAD_S) return;
    if (ready?.key !== next.entry.key || ready.forGeneration !== generation) {
      makeReady(next.entry, plan);
    } else {
      // Task 47: the tempo of a mix follows the playing deck's — a tempo moved, a way back.
      drive(ready.deck, plan.mix?.factor ?? 1);
    }
    const sounding = wantsSound && !cur().paused && state().status === 'playing';
    // Task 47: a mix is armed ahead, and its own interval plays the next deck on the beat.
    const armAtS = plan.mix === undefined ? plan.startAtS : plan.startAtS - MIX_ARM_S;
    if (positionS >= armAtS && sounding && state().problem === undefined && !speed.touched) {
      begin(plan);
    }
  }

  function begin(plan: TransitionPlan, oneShot = false): void {
    const made = ready;
    if (made === undefined) return;
    // Task 47: the playing deck keeps the speed it has: the mix is laid on it.
    stopHoming(false);
    overlap = {
      ready: made,
      from: active,
      plan,
      timer: setInterval(step, WRITE_EVERY_MS),
      switched: false,
      started: false,
      heard: false,
      fromFactor: active.factor,
      fromShare: active.factor * relativeTempo(),
      crossFrom: positions().crossfader,
      crossTo: sideOfDeck(made.deck.side),
      oneShot,
      dub: { depthFrom: undefined, raised: false, echoedOut: false },
    };
    set({ crossfading: true, mixing: plan.mix !== undefined });
    lastAliveAt = now();
    if (plan.mix === undefined) startNew(overlap, plan.startAtS);
    step();
  }

  /**
   * The new deck plays. A mix whose step came later than one step after its start has the new
   * deck start as far on as it is late, so that its beats still fall where they were laid.
   */
  function startNew(going: Overlap, positionS: number): void {
    going.started = true;
    const made = going.ready;
    const forGeneration = generation;
    if (going.plan.mix !== undefined && positionS - going.plan.startAtS > STEP_S) {
      made.deck.el.currentTime = startFromS(going.plan, positionS, going.fromShare);
    }
    void safePlay(made.deck.el).then((outcome) => {
      // `aborted` is a pause that raced the start — the listener's, not a refusal.
      if (outcome.ok || outcome.reason === 'aborted') return;
      if (overlap?.ready !== made || overlap.switched) return;
      // The new deck would not play: this track ends as a cut.
      noOverlapFor = forGeneration;
      abandon();
    });
  }

  /** One look of the overlap: the start of a mix, the two gains, the switch in its middle, the end. */
  function step(): void {
    const going = overlap;
    if (going === undefined) return;
    if (!going.switched && !going.oneShot && upcoming()?.entry.key !== going.ready.key) {
      // The queue was edited: the next track is another one now.
      abandon();
      return;
    }
    const positionS = currentPosition(going.from.el, 0);
    if (!going.started) {
      // Within half a step of the start, or past it: the nearest a step can come to the beat.
      if (going.from.el.paused || positionS + STEP_S / 2 < going.plan.startAtS) return;
      startNew(going, positionS);
      return;
    }
    const frame = frameAt(going.plan, positionS);
    // Task 56: the automat moves the crossfader; the gains are the mixer's.
    moveCrossfader(going.crossFrom + (going.crossTo - going.crossFrom) * frame.progress);
    dubOut(going, positionS);
    if (frame.switched && !going.switched) switchTo(going);
    keepTogether(going, positionS);
    if (frame.done) finish(going);
  }

  /**
   * Task 47: the beats of a mix held together — the new deck follows the old one until the
   * middle, the old one the new after it, by a nudge of half a per cent at most (`lib/mix.ts`).
   */
  function keepTogether(going: Overlap, positionS: number): void {
    const mix = going.plan.mix;
    const next = going.ready.deck;
    if (mix === undefined || !going.heard || next.el.paused || going.from.el.paused) return;
    const share = nudge(phaseErrorS(mix.grid, positionS, currentPosition(next.el, 0)));
    if (!going.switched) {
      drive(next, mix.factor * (1 + share));
      return;
    }
    drive(next, mix.factor);
    drive(going.from, going.fromFactor * (1 - share));
  }

  /** The middle of the overlap: the new track is the one the slice, the queue and the lock screen hold. */
  function switchTo(going: Overlap): void {
    going.switched = true;
    const before = active;
    becomeActive(going.ready.deck, going.ready.track, going.ready.key, () => {
      before.speed.release();
      // Task 47: the new deck holds the tempo it was brought to; the lock screen is told so below.
      if (going.plan.mix !== undefined) drive(active, going.plan.mix.factor);
    });
  }

  /**
   * `deck` is the active one now, with `track` — the queue's entry `key`, when it has one: the
   * slice, the queue, the lock screen and «недавние» learn of it. The middle of an overlap (task
   * 45), and since task 55 a deck the listener plays by hand while the other is silent.
   */
  function becomeActive(
    deck: Deck,
    track: TrackItem,
    key: number | undefined,
    meanwhile: () => void = () => undefined,
  ): void {
    const queue = useQueue.getState();
    const index = key === undefined ? -1 : queue.entries.findIndex((entry) => entry.key === key);
    // «Повтор одного»: the same entry going round — the same listen, not a new one.
    const again = index !== -1 && index === queue.index;
    for (const one of decks) one.manual = false;
    active = deck;
    setActiveDeck(deck.side);
    meanwhile();
    generation += 1;
    sourcedFor = generation;
    reported = true;
    refreshedAfterError = false;
    fetchingSince = undefined;
    cuedAtS = undefined;
    shownS = undefined;
    seekedByHand = false;
    lastAliveAt = now();
    link = track.stream;
    holds(deck, track, key, false);
    if (index !== -1 && !again) queue.setIndex(index);
    const paused = cur().paused;
    wantsSound = !paused;
    set({
      trackId: track.id,
      track,
      status: paused ? 'paused' : 'playing',
      buffering: false,
      problem: undefined,
      positionS: currentPosition(cur(), track.durationS),
      durationS: effectiveDuration(cur(), track.durationS),
      seekLimitS: seekLimit(cur(), track.durationS),
      resumeFromS: undefined,
    });
    announce(track);
    setPlaybackState(session, paused ? 'paused' : 'playing');
    publishPosition();
    if (again) return;
    deps.played(track);
    feed();
  }

  /**
   * The end of the overlap: the old deck silent and paused, the new one at full volume — and after
   * a mix, on its way back to the deck's tempo.
   */
  function finish(going: Overlap): void {
    clearInterval(going.timer);
    overlap = undefined;
    ready = undefined;
    endDub(going);
    if (usePult.getState().held.has('xfader')) {
      // A finger has the crossfader: the old deck may be what is heard, so it plays on as the
      // listener's own, and where the finger leaves the crossfader decides the active deck.
      going.from.manual = true;
    } else {
      moveCrossfader(going.crossTo);
      if (!going.from.el.paused) going.from.el.pause();
    }
    drive(going.from, 1);
    set({ crossfading: false, mixing: false });
    startHoming(going.ready.deck);
  }

  /** Task 47: `deck` back from its factor to the deck's tempo — see `lib/mix.ts`, «Back to the tempo». */
  function startHoming(deck: Deck): void {
    stopHoming(false);
    if (deck.factor === 1) return;
    homing = {
      deck,
      from: deck.factor,
      timer: setInterval(home, WRITE_EVERY_MS),
      elapsedMs: 0,
      lastAt: now(),
    };
  }

  /** One step of the way back: only sound counts, a pause holds it where it is. */
  function home(): void {
    const going = homing;
    if (going === undefined) return;
    const at = now();
    if (!going.deck.el.paused) going.elapsedMs += at - going.lastAt;
    going.lastAt = at;
    const factor = returnFactor(going.from, going.elapsedMs / 1000);
    drive(going.deck, factor);
    if (factor !== 1) return;
    stopHoming(false);
    if (going.deck === active) publishPosition();
  }

  /** Task 47: the way back ends — at the tempo when `toTempo`, else at the speed it has reached. */
  function stopHoming(toTempo: boolean): void {
    const going = homing;
    if (going === undefined) return;
    clearInterval(going.timer);
    homing = undefined;
    if (toTempo) drive(going.deck, 1);
  }

  /** The overlap ends now, the new track taking over at once: «следующий», a tap elsewhere. */
  function settle(): void {
    const going = overlap;
    if (going === undefined) {
      ready = undefined;
      return;
    }
    if (!going.switched) switchTo(going);
    finish(going);
  }

  /** The overlap is called off before its middle: the old track plays on, alone, at full volume. */
  function abandon(): void {
    const going = overlap;
    if (going === undefined) return;
    clearInterval(going.timer);
    overlap = undefined;
    ready = undefined;
    endDub(going);
    if (!going.ready.deck.el.paused) going.ready.deck.el.pause();
    moveCrossfader(going.crossFrom);
    drive(going.ready.deck, 1);
    set({ crossfading: false, mixing: false });
    // Task 47: a track laid on mid-way back from the mix before goes on back.
    startHoming(going.from);
  }

  /** The decks into the context, once it runs — see «Two decks». */
  function routeDecks(): void {
    const context = deps.sound;
    if (context === undefined || decks.length < 2 || decks.some((deck) => deck.gain !== undefined))
      return;
    try {
      for (const deck of decks) {
        deck.gain = context.route(deck.el);
        // A gain node starts at 1: a deck at 1 needs no write.
        deck.lastGain = 1;
      }
    } catch {
      // A deck the browser would not route plays as it did; volumes stay out of reach.
      for (const deck of decks) deck.gain = undefined;
      return;
    }
    for (const deck of decks) {
      applyTone(deck);
      applyEffects(deck);
    }
    mixGains();
  }

  /**
   * Inside a tap, after the active deck's `play()`: the other deck unlocked with silence, and the
   * context made and woken. Harmless outside a tap — a lock-screen button: what the phone
   * refuses there is asked again at the next tap.
   */
  function inTap(): void {
    for (const deck of decks) {
      // Task 55: a deck the listener gave a track keeps it — the silence would take its address.
      if (deck === active || deck.unlocked || deck === ready?.deck || deck.manual) continue;
      deck.el.src = SILENCE_URL;
      void safePlay(deck.el);
    }
    const context = deps.sound?.wake();
    if (context === undefined) return;
    if (context.state() === 'running') {
      routeDecks();
      return;
    }
    void context.resume().then(
      () => {
        if (context.state() === 'running') routeDecks();
      },
      () => undefined,
    );
  }

  /**
   * What the deck that is not active says. See «Two decks»: only three things are read. The
   * listener's own deck (task 55) is left to sound: its `playing` is not paused.
   */
  function onOther(deck: Deck, event: Event): void {
    const going = overlap;
    switch (event.type) {
      case 'timeupdate':
      case 'progress':
        if (going !== undefined) lastAliveAt = now();
        return;
      case 'playing':
        if (going?.ready.deck === deck) going.heard = true;
        if (deck.manual) return;
        // A deck neither active nor in an overlap has no business sounding: the silence of the
        // first tap, above all.
        if (going?.ready.deck !== deck && going?.from !== deck) deck.el.pause();
        return;
      case 'ended':
        if (going?.switched === true && going.from === deck) finish(going);
        return;
      case 'error':
        if (going !== undefined && !going.switched && going.ready.deck === deck) {
          noOverlapFor = generation;
          abandon();
        } else if (going?.switched === true && going.from === deck) {
          finish(going);
        } else if (ready?.deck === deck) {
          noOverlapFor = generation;
          ready = undefined;
        }
        // Task 55: the listener's own deck failed otherwise: it simply stays silent, as a deck would.
        return;
      default:
        return;
    }
  }

  /* ── the interval: position between `timeupdate`s, and the watchdog ──── */

  function tick(): void {
    watchSleep();
    for (const deck of decks) publishDeck(deck);
    const current = state();
    if (current.track === undefined) return;
    // The watchdog covers the request for a fresh URL too: a request that never answers
    // must end in «Повторить», not in «Загружается…» for ever.
    if (fetchingSince !== undefined && now() - fetchingSince >= STALL_AFTER_MS) {
      fetchingSince = undefined;
      generation += 1; // the late answer, if it ever comes, plays nothing
      giveUp('stalled');
      return;
    }
    if (!cur().paused) set({ positionS: shownPosition() });
    if (current.status === 'playing' && now() - lastPersistAt >= SAVE_EVERY_MS) persist();
    lookAhead(current.track);
    // A hand holding the record is silence the listener chose (task 09, `engine/speed.ts`).
    if (!wantsSound || cur().paused || current.problem !== undefined || speed.touched) {
      lastAliveAt = now();
      return;
    }
    if (now() - lastAliveAt >= STALL_AFTER_MS) {
      // Not `giveUp`: the element is left as it is, and if the network comes back by itself
      // the next `playing` clears the problem without a tap.
      wantsSound = false;
      set({ buffering: false, problem: 'stalled' });
    }
  }

  /** Each deck's events: the active one's are the player's, the other one's are read apart. */
  const wiring = decks.map((deck) => ({
    deck,
    listener: (event: Event): void => {
      if (event.type === 'playing') deck.unlocked = true;
      // Task 58: the deck stopped — the end of its cut, whoever stopped it; playing again, of the
      // whole ECHO OUT. A deck that plays on under the cut (a stall's `playing`) stays cut.
      if (event.type === 'pause') endCut(deck);
      if (event.type === 'playing' && deck.echoOut?.cut === false) endEchoOut(deck);
      // Task 47: a new address puts an element's speed back to 1; the deck's own goes back on.
      if (event.type === 'loadstart') drive(deck, deck.factor);
      if (deck === active) onEvent(event);
      else onOther(deck, event);
      // Task 55: the pult shows each deck as it is.
      if (PUBLISHED.has(event.type)) publishDeck(deck);
    },
  }));
  for (const { deck, listener } of wiring) {
    for (const name of MEDIA_EVENTS) deck.el.addEventListener(name, listener);
  }
  const timer = setInterval(tick, TICK_MS);

  /** Task 43: see «The network coming back is one retry». */
  function onOnline(): void {
    const problem = state().problem;
    if (problem === 'stalled' || problem === 'failed') retry();
  }
  window.addEventListener('online', onOnline);

  // Task 23: a deck's tempo is the speed of its record nobody touches, and — the active deck's —
  // the lock screen's. Task 55: each deck its own, and its own KEYLOCK.
  for (const deck of decks) {
    drive(deck, deck.factor);
    deck.speed.setKeylock(useDeck.getState().decks[deck.side].keylock);
  }
  // Task 56: a player starts with the crossfader at its active deck's side — deck A's; and the
  // deck slice learns which deck that is (task 57: the turntable holds that deck ready for the scratch).
  setActiveDeck(active.side);
  moveCrossfader(sideOfDeck(active.side));
  // Task 56: the mixer's knobs and faders into the chains; the louder deck the active one.
  const stopFollowingMixer = usePult.subscribe((now, before) => {
    if (now.values !== before.values || now.effect !== before.effect) {
      for (const deck of decks) {
        applyTone(deck);
        applyEffects(deck);
      }
      mixGains();
      if (!steering) followMixer();
      return;
    }
    // A finger let go of a fader: where it left it decides the active deck.
    if (now.held !== before.held && !steering) followMixer();
  });
  const stopFollowingTempo = useDeck.subscribe((now, before) => {
    for (const deck of decks) {
      const mine = now.decks[deck.side];
      const was = before.decks[deck.side];
      if (mine.keylock !== was.keylock) deck.speed.setKeylock(mine.keylock);
      if (mine.pitch === was.pitch) continue;
      drive(deck, deck.factor);
      if (deck === active && state().track !== undefined) publishPosition();
    }
  });

  /* ── the controls ────────────────────────────────────────────────────── */

  /** The element on `track` from `startS`, with a fresh link first if this one is nearly spent. */
  function startTrack(track: TrackItem, startS: number, forGeneration: number): void {
    if (track.stream.expiresAt - STREAM_SAFETY_S > nowS()) {
      start(track.stream.url, startS);
      return;
    }
    restartFresh(track.id, startS, forGeneration);
  }

  /**
   * Makes `track` the one held and starts it. Synchronous up to `play()`: tap-safe. A start from
   * the beginning of a long track goes on from its place instead — see the header.
   */
  function load(track: TrackItem, fromS: number): void {
    // Task 47: a track started by hand starts at the deck's tempo, whatever a mix left — and,
    // since task 55, whatever PITCH BEND a lost release left on the deck it plays on.
    stopHoming(false);
    active.bend = 1;
    for (const deck of decks) if (deck.factor !== 1 || deck === active) drive(deck, 1);
    const resumed = fromS === 0 ? resumePoint(track.durationS, placeOf(track.id)) : 0;
    const startS = resumed > 0 ? resumed : fromS;
    generation += 1;
    const forGeneration = generation;
    refreshedAfterError = false;
    // A fresh-link request of the track left behind is abandoned with its generation; its
    // clock must not go on to call the new track stalled.
    fetchingSince = undefined;
    cuedAtS = undefined;
    reported = false;
    // A hand's second belongs to the track it was on.
    shownS = undefined;
    link = track.stream;
    const durationS = track.durationS > 0 ? track.durationS : 0;
    set({
      trackId: track.id,
      track,
      status: 'loading',
      buffering: true,
      problem: undefined,
      positionS: startS,
      durationS,
      seekLimitS: durationS,
      resumeFromS: resumed > 0 ? resumed : undefined,
    });
    announce(track);
    syncPosition(session, track.durationS, startS, speed.base);
    holds(active, track, useQueue.getState().entries[useQueue.getState().index]?.key, true);
    // Task 56: a track played from a list is to be heard — the crossfader to its deck's side.
    moveCrossfader(sideOfDeck(active.side));
    startTrack(track, startS, forGeneration);
    seekedByHand = false;
    if (fromS === 0 && isLongTrack(track.durationS)) {
      void refreshPlace(track.id).then((placeS) => {
        goOnFrom(placeS, startS, forGeneration);
      });
    }
  }

  /** Task 49: the server's answer about the place of the track just started — see the header. */
  function goOnFrom(placeS: number | undefined, startS: number, forGeneration: number): void {
    const current = state().track;
    if (placeS === undefined || forGeneration !== generation || current === undefined) return;
    if (seekedByHand || Math.abs(state().positionS - startS) >= RESUME_EDGE_S) return;
    const target = resumePoint(current.durationS, placeS);
    if (target === startS) return;
    seek(target);
    set({ resumeFromS: target > 0 ? target : undefined });
  }

  /** Task 44: the next page of the list, when few tracks are left — see the header. */
  function feed(): void {
    const queue = useQueue.getState();
    const source = queue.source;
    // A queue replaced since has its own list: a page held for the old one is not its.
    if (held !== undefined && !sameSource(held.source, source)) held = undefined;
    if (source === null || deps.nextPage === undefined) return;
    const left = passLeft(queue);
    if (held !== undefined) {
      if (queue.shuffle && left > 0) return;
      const { page } = held;
      held = undefined;
      queue.appendPage(page.items, page.nextCursor);
      return;
    }
    if (feeding === source.cursor || left > FEED_AHEAD) return;
    feeding = source.cursor;
    void deps.nextPage(source).then(
      (page) => {
        feeding = undefined;
        if (page === undefined || !sameSource(source, useQueue.getState().source)) return;
        held = { source, page };
        feed();
      },
      () => {
        feeding = undefined;
      },
    );
  }

  /** The queue's entry at `index`, on the active deck. An overlap under way ends first. */
  function go(index: number): void {
    const entry = useQueue.getState().entries[index];
    if (entry === undefined) return;
    settle();
    useQueue.getState().setIndex(index);
    load(entry.track, 0);
    feed();
  }

  /** {@link go} from a tap: the other deck is unlocked in it too (task 45). */
  function playAt(index: number): void {
    go(index);
    inTap();
  }

  function playFrom(
    list: readonly TrackItem[],
    track: TrackItem,
    source: QueueSource | null = null,
  ): void {
    const at = list.findIndex((item) => item.id === track.id);
    useQueue.getState().replace(at === -1 ? [track] : list.slice(at), at === -1 ? null : source);
    playAt(0);
  }

  function cue(positionS: number): void {
    const queue = useQueue.getState();
    const track = queue.entries[queue.index]?.track;
    if (track === undefined || state().track !== undefined) return;
    generation += 1;
    refreshedAfterError = false;
    wantsSound = false;
    fetchingSince = undefined;
    reported = false;
    link = track.stream;
    const durationS = track.durationS > 0 ? track.durationS : 0;
    const at = durationS > 0 ? Math.min(Math.max(0, positionS), durationS) : Math.max(0, positionS);
    cuedAtS = at;
    set({
      trackId: track.id,
      track,
      status: 'paused',
      buffering: false,
      problem: undefined,
      positionS: at,
      durationS,
      seekLimitS: durationS,
      resumeFromS: undefined,
    });
    announce(track);
    setPlaybackState(session, 'paused');
    syncPosition(session, durationS, at, speed.base);
    holds(active, track, queue.entries[queue.index]?.key, true);
    feed();
  }

  function enqueue(track: TrackItem): boolean {
    const added = useQueue.getState().enqueue(track);
    if (added && state().track === undefined) cue(0);
    return added;
  }

  function playNext(track: TrackItem): boolean {
    const added = useQueue.getState().insertNext(track);
    if (added && state().track === undefined) cue(0);
    return added;
  }

  function resume(): void {
    const current = state();
    if (current.track === undefined) return;
    if (cuedAtS !== undefined) {
      // A held track the element has never been given: its first play, from its second. The
      // link may need renewing first, and the bar says «Загружается…» while it does.
      const at = cuedAtS;
      cuedAtS = undefined;
      set({ status: 'loading', buffering: true, problem: undefined });
      startTrack(current.track, at, generation);
      inTap();
      return;
    }
    if (current.status === 'ended') cur().currentTime = 0;
    const forGeneration = generation;
    wantsSound = true;
    lastAliveAt = now();
    set({ problem: undefined, status: current.status === 'playing' ? 'playing' : 'loading' });
    // `safePlay` calls `play()` before its first `await`: still inside the tap.
    void safePlay(cur()).then((outcome) => {
      afterPlay(outcome, forGeneration);
    });
    // Mid-overlap, the other deck goes on with it (task 45) — once a mix has started it (task 47).
    const partner = overlap?.started === true ? partnerOf(overlap) : undefined;
    if (partner !== undefined) void safePlay(partner.el);
    inTap();
  }

  /** Task 45: the deck of an overlap that is not the active one. */
  function partnerOf(going: Overlap): Deck {
    return going.from === active ? going.ready.deck : going.from;
  }

  /* ── two equal decks (task 55) ──────────────────────────────────────── */

  /**
   * `deck` holds `track` now — the queue's entry `key` when it has one. A new track on a deck
   * clears its CUE (`fresh`): the mark was a second of the track before.
   */
  function holds(deck: Deck, track: TrackItem, key: number | undefined, fresh: boolean): void {
    const changed = deck.track?.id !== track.id;
    deck.track = track;
    deck.entryKey = key;
    if (changed || fresh) updateDeck(deck.side, { cueS: undefined });
    // Task 58: another track is not going out in the echo of the last; its beats are its own.
    if (changed || fresh) endEchoOut(deck);
    applyEffects(deck);
    publishDeck(deck);
  }

  /** A deck's state for the pult: its track, whether it sounds, where it is. */
  function publishDeck(deck: Deck): void {
    const isActive = deck === active;
    const track = isActive ? state().track : deck.track;
    if (track === undefined) {
      updateDeck(deck.side, { track: undefined, playing: false, positionS: 0, durationS: 0 });
      return;
    }
    // A held track has no element behind it yet: the second it waits at.
    const waiting = isActive ? cuedAtS : undefined;
    updateDeck(deck.side, {
      track,
      playing: waiting === undefined && !deck.el.paused,
      positionS: waiting ?? currentPosition(deck.el, track.durationS),
      durationS:
        waiting === undefined ? effectiveDuration(deck.el, track.durationS) : track.durationS,
    });
  }

  function deckOf(side: DeckSide): Deck | undefined {
    return decks.find((deck) => deck.side === side);
  }

  /**
   * The active track ended and the listener's other deck plays: with AUTO, that one goes on as the
   * deck heard — the crossfader to its side. `true` when it did.
   */
  function cutToPlaying(): boolean {
    const spare = other();
    if (spare === undefined || !spare.manual || spare.el.paused) return false;
    takeOver(spare);
    return true;
  }

  function takeOver(deck: Deck): void {
    const track = deck.track;
    if (track === undefined || deck === active) return;
    const going = overlap;
    if (going !== undefined) {
      clearInterval(going.timer);
      overlap = undefined;
      set({ crossfading: false, mixing: false });
    }
    ready = undefined;
    becomeActive(deck, track, deck.entryKey);
    moveCrossfader(sideOfDeck(deck.side));
    // A deck taken over mid-mix goes back to its tempo, as after a mix (story 109).
    startHoming(deck);
  }

  /** The queue's entry for `track`, put next — LOAD with AUTO on. Its key, or none when full. */
  function putNext(track: TrackItem): number | undefined {
    const queue = useQueue.getState();
    const known = new Set(queue.entries.map((entry) => entry.key));
    if (!queue.insertNext(track)) return undefined;
    return useQueue.getState().entries.find((entry) => !known.has(entry.key))?.key;
  }

  /** `deck`'s element on `url` at the end of `track`'s lead-in, paused, at its tempo. */
  function cueElement(deck: Deck, track: TrackItem, url: string): void {
    if (!deck.el.paused) deck.el.pause();
    deck.el.src = url;
    const atS = leadOf(track);
    if (atS > 0) deck.el.currentTime = atS;
    deck.bend = 1;
    drive(deck, 1);
  }

  function loadDeck(side: DeckSide, track: TrackItem): void {
    const deck = deckOf(side);
    if (deck === undefined) return;
    if (deck === active) {
      loadActive(track);
      return;
    }
    loadOther(deck, track);
  }

  /** LOAD on the deck that is not active — see «Two equal decks». */
  function loadOther(deck: Deck, track: TrackItem): void {
    // An overlap into or out of this deck is over: the listener has taken it.
    if (overlap?.switched === true) settle();
    else abandon();
    deck.manual = true;
    // Task 56: with AUTO the track is next in the queue too; off, it is only on the deck.
    const key = isAuto() ? putNext(track) : undefined;
    // The next track of the queue now: the overlap goes into this deck as it stands.
    ready = key === undefined ? undefined : { deck, key, track, forGeneration: generation };
    holds(deck, track, key, true);
    if (track.stream.expiresAt - STREAM_SAFETY_S > nowS()) {
      cueElement(deck, track, track.stream.url);
    } else {
      // A link chosen on the screen long ago: a fresh one first, then the deck — unless the
      // listener has given the deck another track meanwhile.
      if (!deck.el.paused) deck.el.pause();
      void deps.freshStream(track.id).then((fresh) => {
        if (fresh !== 'gone' && fresh !== undefined && deck.track?.id === track.id) {
          cueElement(deck, track, fresh.url);
        }
      });
    }
    inTap();
  }

  /**
   * LOAD on the active deck: the track it played stops, and `track` stands at the end of its
   * lead-in as the queue's current entry — START·STOP plays it. A queue that is full takes
   * nothing, and then nothing stops.
   */
  function loadActive(track: TrackItem): void {
    if (overlap?.switched === true) settle();
    else abandon();
    let key: number | undefined;
    if (isAuto()) {
      key = putNext(track);
      const index = useQueue.getState().entries.findIndex((entry) => entry.key === key);
      if (key === undefined || index === -1) return;
      useQueue.getState().setIndex(index);
    }
    wantsSound = false;
    generation += 1;
    refreshedAfterError = false;
    fetchingSince = undefined;
    reported = false;
    shownS = undefined;
    link = track.stream;
    const atS = leadOf(track);
    const durationS = track.durationS > 0 ? track.durationS : 0;
    set({
      trackId: track.id,
      track,
      status: 'paused',
      buffering: false,
      problem: undefined,
      positionS: atS,
      durationS,
      seekLimitS: durationS,
      resumeFromS: undefined,
    });
    announce(track);
    setPlaybackState(session, 'paused');
    syncPosition(session, durationS, atS, speed.base);
    if (track.stream.expiresAt - STREAM_SAFETY_S > nowS()) {
      // The deck gets the address now: START·STOP is then one `play()`.
      cuedAtS = undefined;
      sourcedFor = generation;
      cueElement(active, track, track.stream.url);
    } else {
      // A spent link is renewed at the first play, as a track restored at launch is.
      if (!cur().paused) cur().pause();
      cuedAtS = atS;
    }
    holds(active, track, key, true);
    feed();
  }

  function toggleDeck(side: DeckSide): void {
    const deck = deckOf(side);
    if (deck === undefined) return;
    if (deck === active) {
      // Task 58: the deck going out of a transition before its middle — the transition is over,
      // the next track alone: a paused deck going out would hold the transition for ever.
      if (overlap?.from === deck && !cur().paused) {
        settle();
        return;
      }
      // Only this deck stops: the listener's other deck plays on, as the mixer lets it be heard.
      if (isSounding(state().status) && !cur().paused) {
        wantsSound = false;
        cur().pause();
      } else {
        resume();
      }
      return;
    }
    if (deck.track === undefined) return;
    if (!deck.el.paused) {
      // The deck of an overlap stopped by hand: the overlap is over, the other track alone.
      const going = overlap;
      if (going?.ready.deck === deck && !going.switched) abandon();
      else if (going?.from === deck && going.switched) finish(going);
      else deck.el.pause();
      publishDeck(deck);
      return;
    }
    if (overlap !== undefined) settle();
    deck.manual = true;
    if (ready?.deck === deck) ready = undefined;
    void safePlay(deck.el);
    inTap();
  }

  /** A deck's track, whether it sounds and its second now; `undefined` with no track. */
  function readDeck(
    deck: Deck,
  ): { readonly track: TrackItem; readonly sounding: boolean; readonly atS: number } | undefined {
    const isActive = deck === active;
    const track = isActive ? state().track : deck.track;
    if (track === undefined) return undefined;
    return {
      track,
      sounding: isActive ? isSounding(state().status) && !cur().paused : !deck.el.paused,
      // A track held at launch waits at its second, as `publishDeck` shows it.
      atS: isActive && cuedAtS !== undefined ? cuedAtS : currentPosition(deck.el, track.durationS),
    };
  }

  function cueDeck(side: DeckSide): void {
    const deck = deckOf(side);
    const now = deck === undefined ? undefined : readDeck(deck);
    if (deck === undefined || now === undefined) return;
    const own = useDeck.getState().decks[side];
    if (now.sounding) {
      const target = own.cueS ?? leadOf(now.track);
      if (deck === active) seek(target);
      else deck.el.currentTime = target;
      publishDeck(deck);
      return;
    }
    updateDeck(side, { cueS: now.atS > 0 ? now.atS : leadOf(now.track) });
  }

  function jumpDeck(side: DeckSide, positionS: number): void {
    const deck = deckOf(side);
    if (deck === undefined || !Number.isFinite(positionS)) return;
    if (deck === active) {
      seek(positionS);
      return;
    }
    if (deck.track !== undefined) place(deck, positionS);
  }

  function whereDeck(side: DeckSide): DeckWhere | undefined {
    const deck = deckOf(side);
    const now = deck === undefined ? undefined : readDeck(deck);
    if (now === undefined) return undefined;
    return { trackId: now.track.id, positionS: now.atS, sounding: now.sounding };
  }

  function bendDeck(side: DeckSide, direction: -1 | 0 | 1): void {
    const deck = deckOf(side);
    if (deck === undefined) return;
    deck.bend = direction === 1 ? BEND_UP : direction === -1 ? BEND_DOWN : 1;
    drive(deck, deck.factor);
    if (deck === active && state().track !== undefined) publishPosition();
  }

  /**
   * The player's pause — «Пауза», the lock screen, the sleep timer: the music stops, on every deck.
   * Since task 55 that is the listener's own deck too, and it goes first, so that the active
   * deck's pause finds nothing to hand the sound over to.
   */
  function pause(): void {
    wantsSound = false;
    for (const deck of decks) {
      if (deck !== active && deck.manual && !deck.el.paused) deck.el.pause();
    }
    cur().pause();
    // Task 45: both decks of an overlap stop, and so do its gains — the curve follows a second.
    const partner = overlap === undefined ? undefined : partnerOf(overlap);
    if (partner !== undefined && !partner.el.paused) partner.el.pause();
  }

  function toggle(): void {
    if (isSounding(state().status)) pause();
    else resume();
  }

  function seek(positionS: number): void {
    if (state().track === undefined || !Number.isFinite(positionS)) return;
    // Task 45: a seek ends an overlap — called off before its middle, finished after it.
    if (overlap?.switched === true) settle();
    else abandon();
    place(active, positionS);
  }

  /**
   * Deck `deck` to `positionS`, as far as it is loaded. The overlap is not this function's: a seek
   * ends it first, a scratch's hand-over leaves it be (task 57).
   */
  function place(deck: Deck, positionS: number): void {
    if (deck !== active) {
      const limit = seekLimit(deck.el, deck.track?.durationS ?? 0);
      deck.el.currentTime = Math.min(Math.max(0, positionS), limit);
      publishDeck(deck);
      return;
    }
    const current = state();
    if (current.track === undefined) return;
    seekedByHand = true;
    if (cuedAtS !== undefined) {
      // Nothing is loaded to seek in: the held second moves, and the first play starts there.
      const limit = serverDuration() > 0 ? serverDuration() : Number.POSITIVE_INFINITY;
      cuedAtS = Math.min(Math.max(0, positionS), limit);
      set({ positionS: cuedAtS });
      return;
    }
    const target = Math.min(Math.max(0, positionS), seekLimit(cur(), serverDuration()));
    cur().currentTime = target;
    lastAliveAt = now();
    set({
      positionS: shownS ?? target,
      ...(current.status === 'ended' ? { status: 'paused' } : {}),
    });
  }

  function showPosition(positionS: number | undefined): void {
    if (positionS === undefined || !Number.isFinite(positionS)) {
      shownS = undefined;
    } else {
      // A voice may be outside the track — before its start, past its end: it shows the edge.
      const durationS = effectiveDuration(cur(), serverDuration());
      shownS = Math.max(0, durationS > 0 ? Math.min(positionS, durationS) : positionS);
    }
    if (state().track === undefined) return;
    // A held track has no element to read: a hand's second, or the one it is held at.
    set({ positionS: cuedAtS === undefined ? shownPosition() : (shownS ?? cuedAtS) });
  }

  /**
   * Task 56: AUTO off, «следующий» or «предыдущий» — one transition from the playing track onto the
   * other deck, now: into the track the listener laid there, or, with none, the queue's next or
   * previous one. A blend, or a mix in the queue's `mix` (`lib/transition.ts`, `planNow`). `false`
   * when it cannot be — nothing playing, a deck not unlocked — and then the button does what it
   * always did.
   */
  function oneShot(direction: 'next' | 'previous'): boolean {
    const spare = other();
    const current = state().track;
    if (spare === undefined || current === undefined || !mayOverlap()) return false;
    if (!isSounding(state().status) || cur().paused) return false;
    const own = spare.manual && spare.track !== undefined ? spare.track : undefined;
    let entry: QueueEntry | undefined;
    if (own === undefined) {
      const queue = useQueue.getState();
      const index = direction === 'next' ? nextIndex(queue, 'manual') : previousIndex(queue);
      entry = index === -1 ? undefined : queue.entries[index];
      if (entry === undefined) return false;
    }
    const track = own ?? entry?.track;
    if (track === undefined) return false;
    // A link about to expire is not laid on a deck: the button then does what it always did.
    if (own === undefined && track.stream.expiresAt - STREAM_SAFETY_S <= nowS()) return false;
    const plan = planNow(useQueue.getState().transition, {
      positionS: currentPosition(cur(), serverDuration()),
      durationS: effectiveDuration(cur(), serverDuration()),
      nextLeadInS: own === undefined ? track.leadInS : currentPosition(spare.el, track.durationS),
      leadInS: current.leadInS,
      beat: current.beat,
      nextBeat: track.beat,
      factor: active.factor * relativeTempo(),
    });
    if (entry !== undefined) makeReady(entry, plan);
    else ready = { deck: spare, key: spare.entryKey ?? -1, track, forGeneration: generation };
    if (plan.mix !== undefined) drive(spare, plan.mix.factor);
    begin(plan, true);
    return true;
  }

  function next(): void {
    // Task 45: mid-overlap, «следующий» finishes it and starts no third track (story 107).
    if (overlap !== undefined) {
      settle();
      return;
    }
    // Task 56: AUTO off — one transition onto the other deck, as the automat would make it.
    if (!isAuto() && oneShot('next')) return;
    const target = nextIndex(useQueue.getState(), 'manual');
    if (target !== -1) playAt(target);
  }

  function previous(): void {
    if (overlap === undefined && !isAuto() && oneShot('previous')) return;
    settle();
    if (state().positionS > RESTART_AFTER_S) {
      seek(0);
      return;
    }
    const target = previousIndex(useQueue.getState());
    if (target !== -1) playAt(target);
  }

  function retry(): void {
    const current = state();
    const track = current.track;
    if (track === undefined || current.problem === 'gone') return;
    if (current.problem === 'gesture') {
      resume();
      return;
    }
    // A fresh URL for every retry: the old one may be the very thing that failed.
    settle();
    generation += 1;
    set({ status: 'loading', buffering: true, problem: undefined });
    restartFresh(track.id, current.positionS, generation);
    inTap();
  }

  const teardownSession = setupMediaSession(session, {
    play: () => {
      resume();
    },
    pause,
    previoustrack: previous,
    nexttrack: next,
    seekto: seek,
    seekbackward: (offsetS) => {
      seek(state().positionS - offsetS);
    },
    seekforward: (offsetS) => {
      seek(state().positionS + offsetS);
    },
  });

  return {
    playFrom,
    playNext,
    playAt,
    enqueue,
    cue,
    toggle,
    pause,
    seek,
    next,
    previous,
    retry,
    save: persist,
    speed,
    showPosition,
    setSleep,
    loadDeck,
    toggleDeck,
    cueDeck,
    jumpDeck,
    whereDeck,
    bendDeck,
    level: () => deps.sound?.level() ?? 0,
    echoOut: (side) => {
      echoOut(side);
    },
    activeSide: () => active.side,
    elementOf: (side) => deckOf(side)?.el,
    speedOf: (side) => {
      const deck = deckOf(side);
      if (deck === undefined) return undefined;
      return deck === active ? speed : deck.speed;
    },
    seekDeck: (side, positionS) => {
      const deck = deckOf(side);
      if (deck !== undefined && Number.isFinite(positionS)) place(deck, positionS);
    },
    soundUrlOf: (side) => {
      const deck = deckOf(side);
      if (deck === undefined) return undefined;
      if (deck === active) return state().track === undefined ? undefined : link?.url;
      return deck.track?.stream.url;
    },
    heldAtOf: (side) => (deckOf(side) === active ? cuedAtS : undefined),
    headOf: (side) => deckOf(side)?.gain?.head,
    resync: () => {
      readNumbers();
      if (state().track === undefined) return;
      const status = state().status;
      if (cur().paused && status === 'playing') set({ status: 'paused' });
      if (!cur().paused && status === 'paused' && state().problem === undefined)
        set({ status: 'playing' });
    },
    dispose: () => {
      clearInterval(timer);
      if (overlap !== undefined) clearInterval(overlap.timer);
      overlap = undefined;
      stopHoming(false);
      if (fading !== undefined) clearInterval(fading);
      window.removeEventListener('online', onOnline);
      stopFollowingTempo();
      stopFollowingMixer();
      for (const { deck, listener } of wiring) {
        if (deck.echoOut !== undefined) clearTimeout(deck.echoOut.timer);
        deck.speed.dispose();
        for (const name of MEDIA_EVENTS) deck.el.removeEventListener(name, listener);
      }
      teardownSession();
    },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * The instance the app uses
 * ──────────────────────────────────────────────────────────────────────────── */

/** The production answer to {@link NextPage}: the list the key names, from the cursor on. */
async function nextPageFromApi(source: QueueSource): Promise<ListPage | undefined> {
  const query = queryOfKey(source.key);
  if (query === undefined) return { items: [], nextCursor: null };
  const result = await fetchTracks(useSession.getState().session?.token, query, source.cursor);
  return result.ok ? result.data : undefined;
}

/** The production answer to {@link FreshStream}: the session's token and the API. */
async function freshStreamFromApi(trackId: number): Promise<StreamLink | 'gone' | undefined> {
  const token = useSession.getState().session?.token;
  const result = await fetchStreamLink(token, trackId);
  if (result.ok) return result.data;
  return result.error.status === 404 || result.error.status === 410 ? 'gone' : undefined;
}

let instance: PlayerController | undefined;

/** The controller, created on first use with the real decks and the app's context. */
export function player(): PlayerController {
  instance ??= createPlayer({
    el: deckA,
    second: deckB,
    sound: appSound(),
    session: resolveMediaSession(),
    freshStream: freshStreamFromApi,
    now: () => Date.now(),
    // The sync the shell started; before it and after it there is nothing to write to.
    persist: () => {
      runningSync()?.flush();
    },
    prefetch: (url) => {
      prefetchHead(url);
    },
    played: (track) => {
      recordPlayed(track.id);
    },
    nextPage: nextPageFromApi,
  });
  return instance;
}

/**
 * Replaces the controller — the web suite's seam (`spec.md` «Швы»: «поддельный
 * аудиоэлемент»). Resets the slice too, so one test's track does not leak into the next.
 */
export function installPlayer(deps: PlayerDeps): PlayerController {
  instance?.dispose();
  usePlayer.setState(IDLE_PLAYER, true);
  instance = createPlayer(deps);
  return instance;
}
